import { create } from 'zustand';
import { CrmField } from '../types';
import { logger } from '@/features/help/utils/logger';
import { toast } from 'react-hot-toast';
import * as formBuilderApi from '../api/form-builder.services';

export interface CrmStore {
  // Supported CRM objects
  supportedCrmObjects: formBuilderApi.CrmObjectOption[];

  // Org-wide settings
  isAddressPicklistOrgEnabled: boolean | null;

  // CRM fields cache
  crmFieldsForObject: Record<string, CrmField[]>;
  isLoadingCrmFieldsForObject: Record<string, boolean>;
  errorCrmFieldsForObject: Record<string, string | null>;

  // CRM object relationships cache
  crmObjectRelationships: Record<string, { parents: any[]; children: any[] }>;
  isLoadingCrmObjectRelationships: Record<string, boolean>;
  errorCrmObjectRelationships: Record<string, string | null>;

  // Actions
  fetchSupportedCrmObjects: () => Promise<void>;
  fetchAddressPicklistSetting: () => Promise<void>;

  fetchCrmFieldsForObject: (objectType: string) => Promise<void>;
  getFieldsForObject: (objectType: string) => CrmField[] | null;
  isLoadingFieldsForObject: (objectType: string) => boolean;

  fetchCrmObjectRelationships: (objectType: string) => Promise<void>;
  getRelationshipsForObject: (objectType: string) => { parents: any[]; children: any[] } | null;
  isLoadingRelationshipsForObject: (objectType: string) => boolean;

  // Helper methods for allowed objects
  getAllowedObjectsForPrimaryObject: (primaryObject: string) => Set<string> | null;

  // New action to fetch all fields
  fetchAllSupportedObjectFields: () => Promise<void>;
}

export const useCrmStore = create<CrmStore>((set, get) => ({
  supportedCrmObjects: [],

  isAddressPicklistOrgEnabled: null,

  crmFieldsForObject: {},
  isLoadingCrmFieldsForObject: {},
  errorCrmFieldsForObject: {},

  crmObjectRelationships: {},
  isLoadingCrmObjectRelationships: {},
  errorCrmObjectRelationships: {},

  fetchSupportedCrmObjects: async () => {
    try {
      logger.info('CrmStore', 'Fetching supported CRM objects');
      const objects = await formBuilderApi.fetchSupportedCrmObjects();
      set({ supportedCrmObjects: objects });
    } catch (error) {
      logger.error('CrmStore', 'Failed to fetch supported CRM objects', { error });
      toast.error('Could not load supported CRM objects.');
    }
  },

  fetchAddressPicklistSetting: async () => {
    try {
      logger.info('CrmStore', 'Fetching org-wide address picklist setting');
      const { isAddressPicklistOrgEnabled } = await formBuilderApi.fetchAddressPicklistSetting();
      set({ isAddressPicklistOrgEnabled });
      logger.success('CrmStore', 'Successfully fetched address picklist setting', {
        isAddressPicklistOrgEnabled,
      });
    } catch (error) {
      logger.error('CrmStore', 'Failed to fetch address picklist setting, defaulting to false.', {
        error,
      });
      set({ isAddressPicklistOrgEnabled: false }); // Default to false on error
    }
  },

  fetchCrmFieldsForObject: async (objectType: string) => {
    const state = get();
    if (state.isLoadingCrmFieldsForObject[objectType]) {
      logger.debug('CrmStore', `Already fetching fields for ${objectType}, skipping.`);
      return;
    }
    if (state.crmFieldsForObject[objectType]) {
      logger.debug('CrmStore', `Fields for ${objectType} already in cache, skipping.`);
      return;
    }

    set((s) => ({
      isLoadingCrmFieldsForObject: { ...s.isLoadingCrmFieldsForObject, [objectType]: true },
      errorCrmFieldsForObject: { ...s.errorCrmFieldsForObject, [objectType]: null },
    }));

    try {
      logger.info('CrmStore', `Fetching CRM fields for ${objectType}`);
      const fields = await formBuilderApi.fetchCrmFieldsForObject(objectType);
      set((s) => ({
        crmFieldsForObject: { ...s.crmFieldsForObject, [objectType]: fields },
        isLoadingCrmFieldsForObject: { ...s.isLoadingCrmFieldsForObject, [objectType]: false },
      }));
    } catch (error: any) {
      logger.error('CrmStore', `Error fetching fields for ${objectType}`, { error });
      set((s) => ({
        isLoadingCrmFieldsForObject: { ...s.isLoadingCrmFieldsForObject, [objectType]: false },
        errorCrmFieldsForObject: { ...s.errorCrmFieldsForObject, [objectType]: error.message },
      }));
    }
  },

  getFieldsForObject: (objectType: string) => {
    return get().crmFieldsForObject[objectType] || null;
  },

  isLoadingFieldsForObject: (objectType: string) => {
    return !!get().isLoadingCrmFieldsForObject[objectType];
  },

  fetchCrmObjectRelationships: async (objectType: string) => {
    const state = get();
    if (state.isLoadingCrmObjectRelationships[objectType]) {
      logger.debug('CrmStore', `Already fetching relationships for ${objectType}, skipping.`);
      return;
    }
    if (state.crmObjectRelationships[objectType]) {
      logger.debug('CrmStore', `Relationships for ${objectType} already in cache, skipping.`);
      return;
    }

    set((s) => ({
      isLoadingCrmObjectRelationships: { ...s.isLoadingCrmObjectRelationships, [objectType]: true },
      errorCrmObjectRelationships: { ...s.errorCrmObjectRelationships, [objectType]: null },
    }));

    try {
      logger.info('CrmStore', `Fetching CRM object relationships for ${objectType}`);
      const relationships = await formBuilderApi.fetchCrmObjectRelationships(objectType);
      set((s) => ({
        crmObjectRelationships: { ...s.crmObjectRelationships, [objectType]: relationships },
        isLoadingCrmObjectRelationships: {
          ...s.isLoadingCrmObjectRelationships,
          [objectType]: false,
        },
      }));
    } catch (error: any) {
      logger.error('CrmStore', `Error fetching relationships for ${objectType}`, { error });
      set((s) => ({
        isLoadingCrmObjectRelationships: {
          ...s.isLoadingCrmObjectRelationships,
          [objectType]: false,
        },
        errorCrmObjectRelationships: {
          ...s.errorCrmObjectRelationships,
          [objectType]: error.message,
        },
      }));
    }
  },

  getRelationshipsForObject: (objectType: string) => {
    return get().crmObjectRelationships[objectType] || null;
  },

  isLoadingRelationshipsForObject: (objectType: string) => {
    return !!get().isLoadingCrmObjectRelationships[objectType];
  },

  getAllowedObjectsForPrimaryObject: (primaryObject: string) => {
    const relationships = get().crmObjectRelationships[primaryObject];
    if (!relationships) return new Set([primaryObject]);

    const allowed = new Set([primaryObject]);
    relationships.parents.forEach((p) => allowed.add(p.objectType));
    relationships.children.forEach((c) => allowed.add(c.objectType));
    return allowed;
  },

  fetchAllSupportedObjectFields: async () => {
    const { supportedCrmObjects, fetchCrmFieldsForObject } = get();
    if (!supportedCrmObjects.length) {
      logger.warn('CrmStore', 'No supported CRM objects available to fetch fields for.');
      return;
    }

    logger.info(
      'CrmStore',
      `Fetching fields for all ${supportedCrmObjects.length} supported objects.`
    );

    // Create a promise for each fetch operation
    const fetchPromises = supportedCrmObjects.map((obj) => fetchCrmFieldsForObject(obj.id));

    // Await all promises to complete
    await Promise.all(fetchPromises);

    logger.success('CrmStore', 'Successfully fetched all fields for supported objects.');
  },
}));
